import { evolve, omit } from 'remeda';

import { type UnityArticle } from '@@api/services/content/schemas/article';
import { DEFAULT_TENANT_ID } from '@@containers/metadata/constants';
import { type ElementType, ELEMENT_TYPES } from '@@plate/serialization/unity/constants';
import { type LeadElement } from '@@plate/serialization/unity/elements/Lead';
import { type TitleElement } from '@@plate/serialization/unity/elements/Title';
import { type TitleHeaderElement } from '@@plate/serialization/unity/elements/TitleHeader';
import { type Element, type UnityElement } from '@@plate/serialization/unity/groups';
import deserializeState from '@@plate/serialization/unity/utils/deserializeState';
import unwrapSerializedState from '@@plate/serialization/unity/utils/unwrapSerializedState';
import wrapSerializedState from '@@plate/serialization/unity/utils/wrapSerializedState';

import { deserializeVariants } from '../../../utils/transformers';
import { type Headings } from '../types';

export const findInContent = <T extends UnityElement | Element>(
    content: UnityElement[] | Element[],
    type: ElementType,
): T | undefined => content.find((o) => o.type === type) as T | undefined;

export const getVariantsFieldsFromContent = (entity: UnityArticle): Headings => {
    const content = unwrapSerializedState(entity.content);

    return content.slice(0, 3) as Headings;
};

export const transformToArticleVariants = (headings: Headings) => {
    const variants = {
        [DEFAULT_TENANT_ID]: {
            headings: wrapSerializedState(headings.map((heading) => omit(heading, ['variants']))),
        },
    };

    headings.forEach((heading) => {
        if (heading.variants) {
            Object.entries(heading.variants).forEach(([variantId, variant]) => {
                Object.assign(variants, {
                    [variantId]: {
                        headings: wrapSerializedState([
                            // @ts-expect-error TS7053 Issue #4120
                            ...(unwrapSerializedState(variants[variantId]?.headings) || []),
                            { ...variant, elementId: heading.elementId },
                        ]),
                    },
                });
            });
        }
    });

    return deserializeVariants(variants, (variant) =>
        evolve(variant, {
            headings: (data) =>
                deserializeState(data) as [TitleHeaderElement, TitleElement, LeadElement],
        }),
    );
};

export const removeUnwantedElements = (content: UnityElement[]) =>
    content.filter(
        (item) =>
            ![ELEMENT_TYPES.TITLE, ELEMENT_TYPES.TITLE_HEADER, ELEMENT_TYPES.LEAD].includes(
                // @ts-expect-error Typing of includes function when used to filter out is very tricky https://fettblog.eu/typescript-array-includes/
                item.type,
            ),
    );
